import { createUrl, getCookie, isProdEnv } from '@vfde-sails/utils';
import { ProductType } from '../enums/product';
import { BasketPath, Cookies, Domains, LandingPagePath, QueryNames } from '../enums/network';
import { getCMSConfig } from './cms';
import { QueryParams } from '../interfaces/network';
import { JarvisVendorType } from '@vfde-sails/avc-core';

export const getQueryString = (data: QueryParams = {}, filterFalsy = true): string => {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(data)) {
        if ((filterFalsy && !value) || null === value || 'undefined' === typeof value) {
            continue;
        }

        params.set(key, value.toString() || '');
    }
    return params.toString() ? `?${params}` : '';
};

/**
 * @deprecated use createUrl() from sails-utils-library,
 * see more https://github.vodafone.com/VFDE-Digital/sails-utils-library/blob/develop/src/url.ts#L93
 */
export const getUrl = (
    domain: string | URL,
    endpoint: string,
    query?: QueryParams,
    filterFalsyQuery = true,
): string => {
    const urlString = isProdEnv() ? domain : '';
    const queryString = getQueryString(query, filterFalsyQuery);

    return `${urlString}${endpoint}${queryString}`;
};

/**
 * Updates the page url with the chosen tariffId
 * and whether gigakombi was selected or not
 * @param gigakombi Wheter gigakombi was selected or not
 * @param tariffId Tariff id chosen
 */
export const updatePageUrl = (gigakombi: boolean, tariffId?: string) => {
    if (!tariffId) { return; }

    const url = new URL(window.location.href);

    url.searchParams.set(QueryNames.TARIFF, tariffId);

    if (gigakombi) {
        url.searchParams.set(QueryNames.GIGAKOMBI, gigakombi.toString());
    } else {
        url.searchParams.delete(QueryNames.GIGAKOMBI);
    }

    history.replaceState(null, '', url.toString());
};

/**
 * Constructs a url to the option page
 * with the given metacode or to the gigakombi
 * mTan check if gigakombi was selected
 * @param metacode Metacode of the chosen product
 * @param gigakombi Whether gigakombi was selected
 * @returns A string containg the url
 */
export const getOptionPageUrl = (metacode: string, gigakombi = false) => {
    const queryParams: QueryParams = {
        [QueryNames.BASKET]: metacode,
        [QueryNames.FF]: getCookie(Cookies.FF),
    };

    if (!gigakombi) {
        return getUrl(Domains.ZUHAUSEPLUS, BasketPath.OPTION, queryParams);
    }

    const contentUrl = getCMSConfig().contentUrls;
    const gigakombiQueryParams: QueryParams = {
        [QueryNames.TARGET]: createUrl(Domains.ZUHAUSEPLUS, BasketPath.OPTION, queryParams),
    };

    return createUrl(Domains.VF, contentUrl.mtan, gigakombiQueryParams);
};

/**
 * Returns the landing page URL for the given tariff
 * @param productType Type of the tariff
 * @param metacode Metacode of the tariff
 * @returns URL of the landing page
 */
export const getProductPageUrl = (productType: JarvisVendorType, metacode: string) => {
    const queryParams: QueryParams = {
        [QueryNames.TARIFF]: metacode,
        [QueryNames.FF]: getCookie(Cookies.FF),
    };

    switch (productType) {
        case ProductType.FIBRE:
            return '';
        case ProductType.CABLE:
            return getUrl(Domains.VF, LandingPagePath.CABLE, queryParams);
        case ProductType.DSL:
            return getUrl(Domains.VF, LandingPagePath.DSL, queryParams);
        default:
            return getUrl(Domains.VF, LandingPagePath.GIGACUBE);
    }
};
/**
 * Checks whether the customer is coming from the 3p LandingPage directly
 *
 * Note 1: Referrer cannot be tested on http addresses like localhost,
 * therefore on localhost this function always returns true
 *
 * Note 2: CMS Config always stores URLs as '/example/page.html', without the origin,
 * to keep it consistent, this function handles that and concatenates the location.origin to it
 */
export const is3pReferrerMatches = (): boolean => isProdEnv()
    ? document.referrer === `${location.origin}${getCMSConfig().landingPage3pUrl}`
    : true;

/**
 * Returns true if customer is in 3P case based on the URL checks
 */
export const isCustomerIn3pFlowByUrl = (): boolean => {
    const url = new URL(location.href);

    if (!url.searchParams.get(QueryNames.THREE_P)) {
        return false;
    }

    // Remove from URL to prevent bookmarking
    url.searchParams.delete(QueryNames.THREE_P);
    history.replaceState(history.state, '', url);

    return is3pReferrerMatches();
};
